import React, { useEffect, useMemo, useState } from 'react';
import { CheckCircle2, Download, FileSpreadsheet, Save, Search, Upload, XCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import { labTestsAPI } from '../../services/api';

const TAB_ORGANISMS = 'organisms';
const TAB_ANTIBIOTICS = 'antibiotics';

const defaultOrganism = { name_en: '', code: '', gram_stain: 'Other', is_active: true };
const defaultAntibiotic = { name_en: '', code: '', family_class: '', is_active: true };

const MicrobiologyDictionary = () => {
  const [activeTab, setActiveTab] = useState(TAB_ORGANISMS);
  const [search, setSearch] = useState('');
  const [organisms, setOrganisms] = useState([]);
  const [antibiotics, setAntibiotics] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [draft, setDraft] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [newRow, setNewRow] = useState(defaultOrganism);

  const activeRows = activeTab === TAB_ORGANISMS ? organisms : antibiotics;

  const loadData = async () => {
    setLoading(true);
    try {
      const [orgRes, abRes] = await Promise.all([
        labTestsAPI.getCultureOrganisms({ search }),
        labTestsAPI.getCultureAntibiotics({ search })
      ]);
      setOrganisms(orgRes.data?.data || []);
      setAntibiotics(abRes.data?.data || []);
    } catch (error) {
      toast.error(error?.response?.data?.message || 'فشل تحميل القواميس');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const timer = setTimeout(() => loadData(), 250);
    return () => clearTimeout(timer);
  }, [search]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setSelectedIds([]);
    setEditingId(null);
    setDraft(null);
    setNewRow(activeTab === TAB_ORGANISMS ? defaultOrganism : defaultAntibiotic);
  }, [activeTab]);

  const allChecked = useMemo(() => activeRows.length > 0 && selectedIds.length === activeRows.length, [activeRows, selectedIds]);

  const onSaveNew = async () => {
    try {
      if (activeTab === TAB_ORGANISMS) await labTestsAPI.createCultureOrganism(newRow);
      else await labTestsAPI.createCultureAntibiotic(newRow);
      toast.success('تمت الإضافة بنجاح');
      setNewRow(activeTab === TAB_ORGANISMS ? defaultOrganism : defaultAntibiotic);
      await loadData();
    } catch (error) {
      toast.error(error?.response?.data?.message || 'فشل الإضافة');
    }
  };

  const onSaveEdit = async () => {
    try {
      if (activeTab === TAB_ORGANISMS) await labTestsAPI.updateCultureOrganism(editingId, draft);
      else await labTestsAPI.updateCultureAntibiotic(editingId, draft);
      toast.success('تم التعديل بنجاح');
      setEditingId(null);
      setDraft(null);
      await loadData();
    } catch (error) {
      toast.error(error?.response?.data?.message || 'فشل التعديل');
    }
  };

  const toggleStatus = async (row) => {
    try {
      if (activeTab === TAB_ORGANISMS) await labTestsAPI.setCultureOrganismStatus(row._id, !row.active);
      else await labTestsAPI.setCultureAntibioticStatus(row._id, !row.active);
      await loadData();
    } catch (error) {
      toast.error(error?.response?.data?.message || 'فشل تحديث الحالة');
    }
  };

  const bulkStatus = async (isActive) => {
    if (!selectedIds.length) return toast.error('اختر عناصر أولاً');
    try {
      if (activeTab === TAB_ORGANISMS) await labTestsAPI.bulkCultureOrganismStatus(selectedIds, isActive);
      else await labTestsAPI.bulkCultureAntibioticStatus(selectedIds, isActive);
      toast.success('تم تحديث العناصر');
      setSelectedIds([]);
      await loadData();
    } catch (error) {
      toast.error(error?.response?.data?.message || 'فشل عملية التحديث الجماعي');
    }
  };

  const downloadTemplate = async () => {
    try {
      const response = activeTab === TAB_ORGANISMS
        ? await labTestsAPI.downloadCultureOrganismTemplate()
        : await labTestsAPI.downloadCultureAntibioticTemplate();
      const fileName = activeTab === TAB_ORGANISMS ? 'organisms-template.csv' : 'antibiotics-template.csv';
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error('فشل تحميل القالب');
    }
  };

  const importFileSubmit = async () => {
    if (!importFile) return toast.error('اختر ملف أولاً');
    try {
      const formData = new FormData();
      formData.append('file', importFile);
      if (activeTab === TAB_ORGANISMS) await labTestsAPI.importCultureOrganisms(formData);
      else await labTestsAPI.importCultureAntibiotics(formData);
      toast.success('تم الاستيراد بنجاح');
      setShowImportModal(false);
      setImportFile(null);
      await loadData();
    } catch (error) {
      toast.error(error?.response?.data?.message || 'فشل الاستيراد');
    }
  };

  return (
    <div className="container mx-auto px-4 py-6" dir="rtl">
      <div className="bg-white border border-slate-200 rounded-2xl shadow-sm">
        <div className="p-4 border-b border-slate-100 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-xl font-bold text-slate-800">قاموس الأحياء الدقيقة (Microbiology Dictionary)</h1>
            <p className="text-sm text-slate-500 mt-1">إدارة موحدة لـ Organisms و Antibiotics مع استيراد جماعي</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setShowImportModal(true)} className="px-3 py-2 rounded-xl bg-indigo-600 text-white text-sm font-medium inline-flex items-center gap-2">
              <Upload className="w-4 h-4" /> استيراد CSV/Excel
            </button>
          </div>
        </div>

        <div className="p-4 border-b border-slate-100 flex flex-wrap items-center gap-2">
          <button onClick={() => setActiveTab(TAB_ORGANISMS)} className={`px-4 py-2 rounded-xl text-sm font-medium ${activeTab === TAB_ORGANISMS ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'}`}>
            Organisms
          </button>
          <button onClick={() => setActiveTab(TAB_ANTIBIOTICS)} className={`px-4 py-2 rounded-xl text-sm font-medium ${activeTab === TAB_ANTIBIOTICS ? 'bg-cyan-100 text-cyan-700' : 'bg-slate-100 text-slate-600'}`}>
            Antibiotics
          </button>
          <div className="mr-auto relative">
            <Search className="w-4 h-4 text-slate-400 absolute right-3 top-1/2 -translate-y-1/2" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={activeTab === TAB_ORGANISMS ? 'بحث بالاسم/الكود/Gram stain' : 'بحث بالاسم/الكود/Family class'}
              className="w-72 pr-9 pl-3 py-2 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-200 outline-none"
            />
          </div>
        </div>

        <div className="p-4 flex items-center gap-2 border-b border-slate-100">
          <button onClick={() => bulkStatus(true)} className="px-3 py-1.5 text-xs rounded-lg border border-emerald-200 text-emerald-700">تفعيل المحدد</button>
          <button onClick={() => bulkStatus(false)} className="px-3 py-1.5 text-xs rounded-lg border border-amber-200 text-amber-700">تعطيل المحدد</button>
        </div>

        <div className="p-4 overflow-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 text-slate-600">
                <th className="p-2 text-right">
                  <input type="checkbox" checked={allChecked} onChange={(e) => setSelectedIds(e.target.checked ? activeRows.map((x) => x._id) : [])} />
                </th>
                <th className="p-2 text-right">Name EN</th>
                <th className="p-2 text-right">Code</th>
                <th className="p-2 text-right">{activeTab === TAB_ORGANISMS ? 'Gram Stain' : 'Family/Class'}</th>
                <th className="p-2 text-right">Status</th>
                <th className="p-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-b border-slate-100 bg-slate-50/50">
                <td className="p-2">+</td>
                <td className="p-2">
                  <input value={newRow.name_en} onChange={(e) => setNewRow((p) => ({ ...p, name_en: e.target.value }))} className="w-full px-2 py-1.5 border border-slate-200 rounded-lg" />
                </td>
                <td className="p-2">
                  <input value={newRow.code} onChange={(e) => setNewRow((p) => ({ ...p, code: e.target.value }))} className="w-full px-2 py-1.5 border border-slate-200 rounded-lg" />
                </td>
                <td className="p-2">
                  {activeTab === TAB_ORGANISMS ? (
                    <select value={newRow.gram_stain} onChange={(e) => setNewRow((p) => ({ ...p, gram_stain: e.target.value }))} className="w-full px-2 py-1.5 border border-slate-200 rounded-lg">
                      <option>Gram Positive</option>
                      <option>Gram Negative</option>
                      <option>Fungi</option>
                      <option>Other</option>
                    </select>
                  ) : (
                    <input value={newRow.family_class} onChange={(e) => setNewRow((p) => ({ ...p, family_class: e.target.value }))} className="w-full px-2 py-1.5 border border-slate-200 rounded-lg" />
                  )}
                </td>
                <td className="p-2"><span className="px-2 py-1 text-xs rounded bg-emerald-50 text-emerald-700">Active</span></td>
                <td className="p-2">
                  <button onClick={onSaveNew} className="px-3 py-1.5 rounded-lg bg-blue-600 text-white text-xs inline-flex items-center gap-1">
                    <Save className="w-3.5 h-3.5" /> حفظ
                  </button>
                </td>
              </tr>

              {loading && (
                <tr><td colSpan="6" className="p-6 text-center text-slate-500">جاري التحميل...</td></tr>
              )}

              {!loading && activeRows.map((row) => {
                const checked = selectedIds.includes(row._id);
                const isEditing = editingId === row._id;
                const rowData = isEditing ? draft : {
                  name_en: row.name,
                  code: row.code || '',
                  gram_stain: row.gram_stain || 'Other',
                  family_class: row.family_class || '',
                  is_active: row.active
                };

                return (
                  <tr key={row._id} className="border-b border-slate-100 hover:bg-slate-50/40">
                    <td className="p-2">
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={(e) => setSelectedIds((prev) => e.target.checked ? [...prev, row._id] : prev.filter((id) => id !== row._id))}
                      />
                    </td>
                    <td className="p-2">
                      <input disabled={!isEditing} value={rowData.name_en} onChange={(e) => setDraft((d) => ({ ...d, name_en: e.target.value }))} className={`w-full px-2 py-1.5 rounded-lg border ${isEditing ? 'border-blue-200' : 'border-transparent bg-transparent'}`} />
                    </td>
                    <td className="p-2">
                      <input disabled={!isEditing} value={rowData.code} onChange={(e) => setDraft((d) => ({ ...d, code: e.target.value }))} className={`w-full px-2 py-1.5 rounded-lg border ${isEditing ? 'border-blue-200' : 'border-transparent bg-transparent'}`} />
                    </td>
                    <td className="p-2">
                      {activeTab === TAB_ORGANISMS ? (
                        <select disabled={!isEditing} value={rowData.gram_stain} onChange={(e) => setDraft((d) => ({ ...d, gram_stain: e.target.value }))} className={`w-full px-2 py-1.5 rounded-lg border ${isEditing ? 'border-blue-200' : 'border-transparent bg-transparent'}`}>
                          <option>Gram Positive</option>
                          <option>Gram Negative</option>
                          <option>Fungi</option>
                          <option>Other</option>
                        </select>
                      ) : (
                        <input disabled={!isEditing} value={rowData.family_class} onChange={(e) => setDraft((d) => ({ ...d, family_class: e.target.value }))} className={`w-full px-2 py-1.5 rounded-lg border ${isEditing ? 'border-blue-200' : 'border-transparent bg-transparent'}`} />
                      )}
                    </td>
                    <td className="p-2">
                      <button onClick={() => toggleStatus(row)} className={`px-2.5 py-1 rounded-full text-xs ${row.active ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
                        {row.active ? 'Active' : 'Inactive'}
                      </button>
                    </td>
                    <td className="p-2">
                      {!isEditing ? (
                        <button onClick={() => { setEditingId(row._id); setDraft(rowData); }} className="px-2.5 py-1 rounded-lg border border-slate-200 text-xs">Edit</button>
                      ) : (
                        <div className="flex items-center gap-1">
                          <button onClick={onSaveEdit} className="px-2.5 py-1 rounded-lg bg-blue-600 text-white text-xs inline-flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" /> Save</button>
                          <button onClick={() => { setEditingId(null); setDraft(null); }} className="px-2.5 py-1 rounded-lg border border-slate-200 text-xs inline-flex items-center gap-1"><XCircle className="w-3.5 h-3.5" /> Cancel</button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {showImportModal && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
          <div className="w-full max-w-xl bg-white rounded-2xl border border-slate-200 shadow-xl">
            <div className="p-4 border-b border-slate-100 flex items-center justify-between">
              <h3 className="font-bold text-slate-800 inline-flex items-center gap-2"><FileSpreadsheet className="w-5 h-5" /> استيراد {activeTab === TAB_ORGANISMS ? 'Organisms' : 'Antibiotics'}</h3>
              <button onClick={() => setShowImportModal(false)} className="text-slate-500 hover:text-slate-700">✕</button>
            </div>
            <div className="p-4 space-y-4 text-sm">
              <div className="p-3 rounded-xl bg-slate-50 border border-slate-200">
                <p className="font-semibold text-slate-700 mb-1">Template Columns</p>
                {activeTab === TAB_ORGANISMS ? (
                  <p className="text-slate-600">name_en, code, gram_stain (Gram Positive | Gram Negative | Fungi | Other)</p>
                ) : (
                  <p className="text-slate-600">name_en, code, family_class</p>
                )}
              </div>
              <button onClick={downloadTemplate} className="px-3 py-2 rounded-xl border border-slate-200 text-slate-700 inline-flex items-center gap-2">
                <Download className="w-4 h-4" /> تحميل القالب
              </button>
              <input type="file" accept=".csv,.xlsx,.xls" onChange={(e) => setImportFile(e.target.files?.[0] || null)} className="block w-full text-sm" />
            </div>
            <div className="p-4 border-t border-slate-100 flex justify-end gap-2">
              <button onClick={() => setShowImportModal(false)} className="px-4 py-2 rounded-xl border border-slate-200 text-slate-700">إلغاء</button>
              <button onClick={importFileSubmit} className="px-4 py-2 rounded-xl bg-indigo-600 text-white">استيراد</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MicrobiologyDictionary;
